import { useState, useEffect, memo, useMemo, useCallback, useRef } from "react";
import { flushSync } from "react-dom";
import { ROLES } from "../constant/roles";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import SelectProjectTemplate from "../components/SelectProjectTemplate";
import {
  useDeleteProject,
  useDuplicateProject,
  useProjectTemplatesQuery,
  useDuplicateProjectTemplate,
  useDeleteProjectTemplateMutation,
  GetTemplatesParams,
  useCloseProject,
} from "../api/projectManagementApi";
import {
  useEnhancedDatasetsQuery,
  GetEnhancedDatasetsParams,
} from "../api/createDatasetApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../App";
import { Badge } from "../components/ui/badge";
import {
  getStatusBadge,
  getDifficultyBadge,
} from "../utils/projectManagement.utils";
import { Progress } from "../components/ui/progress";
import { PageHeader } from "../components/ui/page-header";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Trash2,
  Edit,
  Eye,
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  Clock,
  Ban,
  Calendar,
  User,
  FileText,
  Link,
  Layers,
  FileStack,
  FileSymlink,
  X,
  MoreVertical,
  File,
  FolderGit2,
  EyeIcon,
} from "lucide-react";
import Frame from "../../src/assets/Frame.png";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { ImageCache } from "./ImageCache";
import { useDebounce } from "../hooks/useDebounce";
import { format, parseISO } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { MaterialTable } from "../components/ui/MaterialTable";
import type { MRT_ColumnDef } from "material-react-table";
import {
  useLTIContextsQuery,
  useLTIContextMembersQuery,
  useUnassignedResourceLinksQuery,
  useLinkProjectMutation,
} from "../api/ltiApi";
import { useNavigate, useLocation } from "react-router-dom";
import paths from "../routes";
import { useProjectLink } from "../contexts/ProjectLinkContext";
import AddNewDatasetPage from "./AddNewDatasetPage";
import {
  useDatasetByIdQuery,
  getDatasetById,
  useDeleteDataset,
} from "../api/createDatasetApi";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import WarningDialog from "../components/ui/WarningDialog";
import { useConvertProjectToTemplateMutation } from "../api/createProjectApi";

interface TemplateData {
  id: string;
  name: string;
  description: string;
  projectData: any;
  createdAt: string;
  createdBy: string;
  category: string;
  tags: string[];
  usageCount: number;
}

export default function ManageDataSets() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setProjectLinkParams } = useProjectLink();
  const [activeTab, setActiveTab] = useState<
    "all-projects" | "templates" | "analytics" | "datasets"
  >("all-projects");
  const [viewMode] = useState<"grid" | "list">("grid");
  const [templateDialogProjectId, setTemplateDialogProjectId] = useState<
    string | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProjectIdToLink, setSelectedProjectId] = useState("");
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<
    string | undefined
  >(undefined);
  const [isOpenSelectTemplate, setIsSelectTemplate] = useState(false);
  const [deleteProjectId, setDeleteProjectId] = useState<string | null>(null);
  const { mutate: duplicateProjectMutation } = useDuplicateProject();
  const [closeProjectId, setCloseProjectId] = useState<string | null>(null);
  const { mutateAsync } = useConvertProjectToTemplateMutation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [courseFilter] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTagFilter, setSelectedTagFilter] = useState("");
  const [isModalOpen2, setIsModalOpen2] = useState(false);
  const [createdBy] = useState<string | undefined>(undefined);
  const [creationType, setCreationType] = useState<
    "newProject" | "useTemplate"
  >("newProject");
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [isViewDetailsDialogOpen, setIsViewDetailsDialogOpen] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState<any>(null);

  // New states for edit/delete modals (matching ViewDatasetPage)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [loadingDatasetIds, setLoadingDatasetIds] = useState(new Set<string>());

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showProjectsWarning, setShowProjectsWarning] = useState(false);
  const [datasetToDelete, setDatasetToDelete] = useState<any>(null);
  const [linkedProjects, setLinkedProjects] = useState<string[]>([]);
  const [selectedDatasetForEdit, setSelectedDatasetForEdit] =
    useState<any>(null);

  const { mutate: deleteDatasetMutation, isPending: isDeleting } =
    useDeleteDataset();

  const handleEditClick = async (datasetIdParam: any) => {
    // 🔒 Defensive: ensure string + non-empty
    if (!datasetIdParam) {
      console.error("[ERROR] datasetIdParam is null/undefined/empty");
      toast.error("Invalid dataset ID");
      return;
    }

    let datasetId = String(datasetIdParam).trim();

    if (!datasetId) {
      console.error("[ERROR] datasetId empty after trim");
      toast.error("Invalid dataset ID");
      return;
    }

    // Fix common format issues
    if (!datasetId.includes("-")) {
      // Try parsing as number if it's numeric string
      const numId = parseInt(datasetId, 10);
      if (!isNaN(numId)) {
        console.log("[DEBUG] Treating as numeric ID, need UUID lookup");
        toast.error("Dataset ID requires UUID format. Please refresh page.");
        return;
      }
    }

    // Normalize UUID casing (backend may be case-sensitive)
    datasetId = datasetId.toLowerCase();

    // 🛡️ UUID v4 validation (matches backend requiredUUID)
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const isValidUUID = uuidRegex.test(datasetId);

    // Store for browser debugging
    (window as any).lastDatasetId = datasetId;

    if (!isValidUUID) {
      console.error(
        "[ERROR] Invalid datasetId - does not match UUID v4 format",
      );
      console.error("Expected: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx");
      console.error("Got:", datasetId);
      toast.error(
        `❌ Invalid Dataset ID: ${datasetId.substring(0, 8)}...\nExpected UUID format`,
      );
      return;
    }
    // console.log("Edit Clicked");

    try {
      const toastId = toast.loading("Loading dataset details..."); // ✅ store ID

      const datasetData = await getDatasetById(datasetId);

      // console.log("[✅ SUCCESS] Dataset loaded:", datasetData?.id);

      setSelectedDatasetForEdit(datasetData);
      setIsEditModalOpen(true);

      // ✅ Update same toast to success (auto replaces loader)
      toast.success("Dataset loaded successfully", { id: toastId });
    } catch (error: any) {
      const toastId = toast.loading("Loading dataset details...");
      console.error(
        "[API ERROR] getDatasetById failed:",
        error.response?.status,
        error.response?.data,
      );

      let errorMsg = "Failed to load dataset for editing";

      if (error.response?.status === 400) {
        errorMsg = `Backend rejected ID "${datasetId.substring(0, 8)}..." (400 Bad Request)`;
      } else if (error.response?.data?.data?.message) {
        errorMsg = error.response.data.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }

      // ✅ Replace loader with error toast
      toast.error(errorMsg, { id: toastId });
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleDelete = async (dataset: any) => {
    const datasetId = dataset;
    console.log("datasetId", datasetId);
    try {
      const fullDataset = await getDatasetById(datasetId);
      console.log("fullDataset", fullDataset);
      setDatasetToDelete(fullDataset);
      if (fullDataset?.projects?.length && fullDataset.projects.length > 0) {
        setLinkedProjects(
          fullDataset.projects.map(
            (p: any) => p.title || p.name || p.id || "Unnamed Project",
          ) || [],
        );
        setShowProjectsWarning(true);
      } else {
        setConfirmOpen(true);
      }
    } catch (error) {
      console.error("Failed to fetch dataset details:", error);
      toast.error("Failed to load dataset details. Cannot delete.");
    }
  };

  const onConfirmDelete = () => {
    if (!datasetToDelete?.id) return;

    deleteDatasetMutation(datasetToDelete.id, {
      onSuccess: () => {
        toast.success("Dataset deleted successfully");
        refetchProjects();
        setConfirmOpen(false);
        setDatasetToDelete(null);
        setLinkedProjects([]);
        setShowProjectsWarning(false);
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.data?.message || "Delete failed");
      },
    });
  };

  const handleViewDataset = useCallback(
    (datasetId: string) => {
      navigate(paths.toViewDataset(datasetId));
    },
    [navigate],
  );

  const handleModalClose = () => {
    setIsModalOpen2(false);
    setCreationType("newProject");
  };
  const handleContinue = () => {
    setIsModalOpen2(false);
    if (creationType === "useTemplate") {
      setShowTemplateSelector(true);
    } else {
      navigate(`${paths.createProject}?mode=create`);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlCourseId = searchParams.get("courseId");
    const urlResourceLinkId = searchParams.get("resourceLinkId");

    if (urlCourseId && urlResourceLinkId) {
      setProjectLinkParams(urlCourseId, urlResourceLinkId);
    }
  }, [location.search, setProjectLinkParams]);

  const projectQueryParams: GetEnhancedDatasetsParams = {
    page,
    limit,
    ...(searchQuery ? { search: searchQuery } : {}),
    ...(selectedTagFilter ? { tags: [selectedTagFilter] } : {}),
    ...(courseFilter ? { courseId: courseFilter } : {}),
    ...(createdBy ? { createdBy } : {}),
  };

  const {
    data: enhancedDatasetsData,
    isLoading: projectsLoading,
    isError: projectsError,
    refetch: refetchProjects,
  } = useEnhancedDatasetsQuery(projectQueryParams as GetEnhancedDatasetsParams);

  // 🐛 DEBUG: Log first dataset structure
  useEffect(() => {
    if (enhancedDatasetsData?.datasets?.[0]) {
    }
  }, [enhancedDatasetsData]);

  // Extract projects and pagination from the API response
  const projects = enhancedDatasetsData?.datasets || [];
  const pagination = enhancedDatasetsData?.pagination || {};

  const { mutate: deleteTemplateMutation, isPending: isDeletingTemplate } =
    useDeleteProjectTemplateMutation({
      onSuccess: () => {
        toast.success("Template deleted successfully");
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Failed to delete template",
        );
      },
    });

  const handleDeleteTemplate = (templateId: string) => {
    deleteTemplateMutation(templateId);
  };

  const [inputValue, setInputValue] = useState("");

  // Debounce the input value for API calls
  const debouncedSearchQuery = useDebounce(inputValue, 1000);

  // Sync searchQuery with debounced value
  useEffect(() => {
    setSearchQuery(debouncedSearchQuery);
    setPage(1);
  }, [debouncedSearchQuery]);

  // Reset page when tag filter changes
  useEffect(() => {
    setPage(1);
  }, [selectedTagFilter]);

  // Sync inputValue when searchQuery changes externally
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  // Handle search input changes
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    [],
  );

  interface TemplateFormData {
    title: string;
    description: string;
    category: string;
    subcategory: string;
    skills_covered: string[];
    technologies_used: string[];
  }

  interface ConvertToTemplateDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    projectId?: string | number;
  }

  const ConvertToTemplateDialog = ({
    isOpen,
    onOpenChange,
    projectId,
  }: ConvertToTemplateDialogProps) => {
    const [formData, setFormData] = useState<TemplateFormData>({
      title: "",
      description: "",
      category: "",
      subcategory: "",
      skills_covered: [],
      technologies_used: [],
    });
    const [skillInput, setSkillInput] = useState("");
    const [techInput, setTechInput] = useState("");

    const handleAddSkill = () => {
      if (
        skillInput.trim() &&
        !formData.skills_covered.includes(skillInput.trim())
      ) {
        setFormData({
          ...formData,
          skills_covered: [...formData.skills_covered, skillInput.trim()],
        });
        setSkillInput("");
      }
    };

    const handleRemoveSkill = (skill: string) => {
      setFormData({
        ...formData,
        skills_covered: formData.skills_covered.filter((s) => s !== skill),
      });
    };

    const handleAddTech = () => {
      if (
        techInput.trim() &&
        !formData.technologies_used.includes(techInput.trim())
      ) {
        setFormData({
          ...formData,
          technologies_used: [...formData.technologies_used, techInput.trim()],
        });
        setTechInput("");
      }
    };

    const handleRemoveTech = (tech: string) => {
      setFormData({
        ...formData,
        technologies_used: formData.technologies_used.filter((t) => t !== tech),
      });
    };

    const resetForm = () => {
      setFormData({
        title: "",
        description: "",
        category: "",
        subcategory: "",
        skills_covered: [],
        technologies_used: [],
      });
      setSkillInput("");
      setTechInput("");
    };

    const handleSubmit = async () => {
      if (!isFormValid()) return;

      try {
        const response = await mutateAsync({
          projectId: String(projectId),
          body: formData,
        });
        if (response.isSuccess) {
          toast.success("Dataset-repo converted to template successfully!");
        }
        onOpenChange(false);
        resetForm();
      } catch (error: any) {
        toast.error(
          error?.response?.data?.data?.message || "Failed to convert dataset",
        );
      }
    };

    const handleCancel = () => {
      onOpenChange(false);
      resetForm();
    };

    const isFormValid = () => {
      return (
        formData.title.trim() !== "" &&
        formData.description.trim() !== "" &&
        formData.category.trim() !== "" &&
        formData.subcategory.trim() !== "" &&
        formData.skills_covered.length > 0 &&
        formData.technologies_used.length > 0
      );
    };

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 bg-gray-400/20 flex items-center justify-center z-[9999] overflow-hidden"
        onClick={handleCancel}
      >
        <div
          className="w-full max-w-2xl m-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="rounded-lg border max-h-[90vh] overflow-y-auto bg-white">
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  Convert dataset to Template
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Fill in the details to create a template from this dataset.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">
                    Title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="title"
                    placeholder="e.g., Data Analysis Template"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    autoComplete="off"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    placeholder="Describe what this template is for..."
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">
                      Category <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="category"
                      placeholder="e.g., Data Science"
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value })
                      }
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subcategory">
                      Subcategory <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="subcategory"
                      placeholder="e.g., Analysis"
                      value={formData.subcategory}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subcategory: e.target.value,
                        })
                      }
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="skills">
                    Skills Covered <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="skills"
                      placeholder="Add a skill (e.g., Python)"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddSkill();
                        }
                      }}
                      autoComplete="off"
                    />
                    <Button
                      type="button"
                      onClick={handleAddSkill}
                      className="bg-bits-blue text-white hover:bg-bits-blue-700"
                    >
                      Add
                    </Button>
                  </div>
                  {formData.skills_covered.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.skills_covered.map((skill, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                        >
                          {skill}
                          <button
                            onClick={() => handleRemoveSkill(skill)}
                            className="hover:bg-blue-200 rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="technologies">
                    Technologies Used <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="technologies"
                      placeholder="Add a technology (e.g., Jupyter)"
                      value={techInput}
                      onChange={(e) => setTechInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTech();
                        }
                      }}
                      autoComplete="off"
                    />
                    <Button
                      type="button"
                      onClick={handleAddTech}
                      className="bg-bits-blue text-white hover:bg-bits-blue-700"
                    >
                      Add
                    </Button>
                  </div>
                  {formData.technologies_used.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.technologies_used.map((tech, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm"
                        >
                          {tech}
                          <button
                            onClick={() => handleRemoveTech(tech)}
                            className="hover:bg-green-200 rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <Button
                  onClick={handleCancel}
                  className="flex-1"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  disabled={!isFormValid()}
                  type="submit"
                  onClick={handleSubmit}
                  className="flex-1 bg-bits-blue hover:bg-bits-blue"
                >
                  Create Template
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Column definitions for MaterialTable
  const columns: MRT_ColumnDef<any>[] = useMemo(
    () => [
      {
        accessorKey: "coverPhotoUrl",
        header: "Cover",
        size: 80,
        Cell: ({ row }) => {
          const coverPhotoUrl = row.original.coverPhotoUrl as string;
          return (
            <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center rounded overflow-hidden">
              {coverPhotoUrl ? (
                <ImageCache
                  src={coverPhotoUrl}
                  alt={row.original.title || "Cover"}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  fetchPriority="low"
                />
              ) : (
                <ImageWithFallback
                  src={Frame}
                  alt="Default Cover"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "title",
        header: "Name",
        Cell: ({ row }) => {
          const title = row.original.title as string;
          const projectId = row.original.projectId as string;
          const displayText = `${title}`;
          const shouldShowTooltip = displayText && displayText.length > 40;

          const content = (
            <div className="flex flex-col">
              <div className="max-w-[250px] truncate cursor-help text-black">
                {displayText}
              </div>
              <div className="text-gray-500 text-xs mt-1">
                dataset ID - {projectId}
              </div>
            </div>
          );

          if (shouldShowTooltip) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>{content}</TooltipTrigger>
                <TooltipContent>
                  <p>{displayText}</p>
                  <p className="text-xs text-gray-400">
                    dataset ID - {projectId}
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          }

          return content;
        },
      },
      {
        accessorKey: "course.name",
        header: "Course",
        Cell: ({ cell }) => (
          <span className="text-black">{String(cell.getValue() ?? "")}</span>
        ),
      },
      {
        accessorKey: "ltiResourceLinks",
        header: "LTI Reference",
        Cell: ({ row }) => {
          const ltiLinks = row.original.ltiResourceLinks || [];
          return (
            <span className="text-black text-center">
              {ltiLinks.length > 0 ? `${ltiLinks[0].resourceLinkTitle}` : "-"}
            </span>
          );
        },
      },
      {
        accessorKey: "createdBy.name",
        header: "Created By",
        Cell: ({ cell }) => (
          <span className="capitalize text-black">
            {String(cell.getValue() ?? "N/A")}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ cell }) => {
          const status = String(cell.getValue() ?? "").toLowerCase();

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="capitalize text-black cursor-default">
                  {getStatusBadge(status)}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {status === "active"
                    ? "This dataset is currently active and accepting submissions."
                    : status === "draft"
                      ? "This dataset is in draft mode and not yet published."
                      : status === "closed"
                        ? "This dataset has been closed explicitly by the instructor or the creator."
                        : status === "deadline passed"
                          ? "The due date for this dataset has passed and not accepting submissions."
                          : "Status information unavailable."}
                </p>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "difficultyLevel",
        header: "Difficulty",
        Cell: ({ cell }) => (
          <span className="text-black">
            {getDifficultyBadge(String(cell.getValue() ?? ""))}
          </span>
        ),
      },
      {
        accessorKey: "submissionCount",
        header: "Submissions",
        Cell: ({ cell }) => (
          <div className="flex items-center justify-center w-10 text-sm text-black bg-bits-grey-100 rounded-full">
            {String(cell.getValue() ?? "")}
          </div>
        ),
      },
      {
        accessorKey: "dueDate",
        header: "Due Date",
        Cell: ({ cell }) => (
          <span className="text-sm text-black">
            {String(cell.getValue() ?? "-")}
          </span>
        ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        enableSorting: false,

        Cell: ({ row }) => {
          const dataset = row.original;

          // 🐛 DEBUG: Log dataset.id when rendering action buttons (before click)
          console.log(
            "[DEBUG TABLE] Dataset row ID:",
            dataset.id,
            "Full row:",
            dataset,
          );

          return (
            <div className="flex items-center gap-2 action-buttons">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => handleViewDataset(dataset.id)}
                  >
                    <Eye className="h-6 w-6 text-gray-600 hover:text-gray-800" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View dataset details</p>
                </TooltipContent>
              </Tooltip>
              {user?.role !== "ta" && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        disabled={
                          dataset.isLinked === true ||
                          dataset.status !== "draft"
                        }
                        onClick={() => {
                          setSelectedProjectId(dataset.id);
                          setSelectedCreatedBy(dataset?.createdBy?.id);
                          setIsModalOpen(true);
                        }}
                      >
                        <Link className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {dataset.isLinked
                          ? "dataset already linked"
                          : "Link dataset to LTI"}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => duplicateProjectMutation(dataset.id)}
                      >
                        <FileStack className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Duplicate dataset</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => handleDelete(dataset)}
                        disabled={isDeleting}
                      >
                        <Trash2 className="h-5 w-5 text-[#D92D20]" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Delete dataset</p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-8 w-8 p-0 ${
                          dataset.status.toLowerCase() === "active"
                            ? "opacity-50"
                            : ""
                        }`}
                        onClick={() =>
                          handleEditClick(String(dataset.id ?? ""))
                        }
                      >
                        <Edit className="h-5 w-5 text-[#2076D1]" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Edit dataset</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setTemplateDialogProjectId(dataset.id)}
                      >
                        <FileSymlink className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Convert to Template</p>
                    </TooltipContent>
                  </Tooltip>
                  {dataset.status.toLowerCase() !== "closed" && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => {
                            setCloseProjectId(dataset.id);
                          }}
                        >
                          <Ban className="h-5 w-5 text-gray-600 hover:text-gray-800" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Close dataset</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {closeProjectId && (
                    <CloseProjectModal
                      isOpen={!!closeProjectId}
                      onClose={() => setCloseProjectId(null)}
                      projectId={closeProjectId}
                      projectName={
                        projects.find((p: any) => p.id === closeProjectId)
                          ?.title ?? "Untitled dataset"
                      }
                    />
                  )}
                </>
              )}
            </div>
          );
        },
      },
    ],
    [
      navigate,
      user?.role,
      isModalOpen,
      selectedProjectIdToLink,
      refetchProjects,
      duplicateProjectMutation,
      deleteProjectId,
      projects,
      templateDialogProjectId,
      closeProjectId,
    ],
  );

  interface AllProjectsTabProps {
    projects: any;
    pagination: any;
    projectsLoading: boolean;
    projectsError: any;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    limit: number;
    setLimit: React.Dispatch<React.SetStateAction<number>>;
    inputValue: string;
    handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    navigate: (path: string) => void;
  }

  const uniqueTags = useMemo(() => {
    const allTags = projects.flatMap((project: any) => project.tags || []);
    const unique = Array.from(
      new Set(allTags.map((tag: string) => tag.toLowerCase())),
    )
      .map((tag) => {
        // Find original casing
        const original = allTags.find((t: string) => t.toLowerCase() === tag);
        return original || tag;
      })
      .sort();
    return unique;
  }, [projects]);

  const renderAllProjectsTab = useCallback(
    ({
      projects,
      pagination,
      projectsLoading,
      projectsError,
      page,
      setPage,
      limit,
      setLimit,
      inputValue,
      handleSearchChange,
      navigate,
    }: AllProjectsTabProps) => {
      return (
        <div className="space-y-6 bg-white">
          <Card className="border-0">
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4 justify-between">
                <div className="flex flex-wrap gap-4 flex-1">
                  <div className="relative min-w-40 w-64">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search name, description..."
                      className="pl-10"
                      value={inputValue}
                      onChange={handleSearchChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault(); // ✅ Prevent form submission
                        }
                      }}
                    />
                  </div>
                  <div className="min-w-40 w-48">
                    <Select
                      value={selectedTagFilter}
                      onValueChange={(value) => {
                        setSelectedTagFilter(value === "all" ? "" : value);
                        setPage(1);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by tag" />
                      </SelectTrigger>
                      <SelectContent>
                        {uniqueTags.map((tag) => (
                          <SelectItem key={tag} value={tag}>
                            {tag}
                          </SelectItem>
                        ))}
                        <SelectItem value="all">All tags</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* <Select
                    value="all-tags"
                    onValueChange={() => {
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-auto sm:min-w-32">
                      <SelectValue placeholder="All tags" />
                    </SelectTrigger>
                    <SelectContent className="text-md ">
                      <SelectItem value="all-tags">All tags</SelectItem>
                    </SelectContent>
                  </Select> */}

                  {/* <Select
                    value="all-time"
                    onValueChange={() => {
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-auto sm:min-w-32">
                      <SelectValue placeholder="All time" />
                    </SelectTrigger>
                    <SelectContent className="text-md ">
                      <SelectItem value="all-time">All time</SelectItem>
                    </SelectContent>
                  </Select> */}
                </div>
              </div>
            </CardContent>
          </Card>

          {viewMode === "grid" && (
            <div className="grid lg:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4 gap-6 will-change-transform">
              {projectsLoading ? (
                <div className="col-span-full text-center py-12">
                  <p>Loading datasets...</p>
                </div>
              ) : projectsError ? (
                <div className="col-span-full text-center py-12">
                  <p>Failed to load datasets</p>
                </div>
              ) : projects && projects.length > 0 ? (
                projects.map((dataset: any) => (
                  <Card
                    key={dataset.id}
                    className="overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <CardContent className="p-4 relative">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="absolute top-2 right-2 h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4 text-gray-800" />
                          </button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                          side="left"
                          align="start"
                          className="w-32"
                        >
                          <DropdownMenuItem
                            disabled={isEditLoading}
                            onClick={() =>
                              handleEditClick(String(dataset.id ?? ""))
                            }
                            className="text-black-800 focus:text-blue-400"
                          >
                            {isEditLoading ? (
                              <span className="flex items-center">
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-400 mr-2"></div>
                                Loading...
                              </span>
                            ) : (
                              <>
                                <Edit className="mr-2 h-4 w-4 focus:text-blue-400" />
                                Edit
                              </>
                            )}
                          </DropdownMenuItem>

                          <DropdownMenuItem
                            onClick={() =>
                              handleDelete(String(dataset.id ?? ""))
                            }
                            className="text-black-800 focus:text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4 focus:text-red-600" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <div
                        className="flex  items-start cursor-pointer"
                        onClick={() => handleViewDataset(dataset.id)}
                      >
                        <div className="w-[10rem] h-[9rem] bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center rounded overflow-hidden">
                          {dataset.coverPhotoUrl ? (
                            <ImageCache
                              src={dataset.coverPhotoUrl}
                              alt={dataset.title || "Cover"}
                              width="100%"
                              height="100%"
                              loading={projectsLoading ? "eager" : "lazy"}
                              fetchPriority={projectsLoading ? "high" : "auto"}
                            />
                          ) : (
                            <ImageWithFallback
                              src={Frame}
                              alt="Default Cover"
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div className="ml-2">
                          <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                            {dataset.title}
                          </h3>

                          <div className="text-sm text-gray-600 mb-3">
                            <p>
                              by{" "}
                              <span className="font-medium">
                                {dataset.uploader?.name || "Unknown"}
                              </span>
                              {" • Updated on "}
                              {dataset.updatedAt
                                ? format(
                                    parseISO(dataset.updatedAt),
                                    "dd/MM/yyyy",
                                  )
                                : "N/A"}
                            </p>
                          </div>

                          <div className="flex flex-wrap gap-2 mb-3">
                            {dataset?.tags?.slice(0, 3).map((tag: string) => (
                              <Badge
                                key={tag}
                                variant="secondary"
                                className="text-xs"
                              >
                                {tag}
                              </Badge>
                            ))}
                            {dataset?.tags && dataset.tags.length > 3 && (
                              <Badge
                                variant="secondary"
                                className="text-xs text-gray-500"
                              >
                                +{dataset.tags.length - 3}
                              </Badge>
                            )}
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex gap-2 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <FolderGit2 className="h-4 w-4" />
                                <span>{dataset?.linkedProjectsCount || 0}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <File className="h-4 w-4" />
                                <span>{dataset?.files.length || 0}</span>
                              </div>
                            </div>

                            {/* <div className="flex items-center gap-1">
                              <EyeIcon className="h-4 w-4 text-[#2076D1]" />
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 h-auto text-[#2076D1] border-0"
                                onClick={() => {
                                  navigate(paths.toViewDataset(dataset.id));
                                }}
                              >
                                View Details
                              </Button>
                            </div> */}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p>No datasets found</p>
                </div>
              )}
            </div>
          )}

          {/* List View */}
          {viewMode === "list" && (
            <MaterialTable
              columns={columns}
              data={projects || []}
              isLoading={projectsLoading}
              error={projectsError}
              emptyMessage={
                projectsLoading
                  ? "Loading projects..."
                  : projectsError
                    ? "Failed to load projects"
                    : "No projects found"
              }
              enableColumnPinning={true}
              stickyColumns={{ right: ["actions"] }}
            />
          )}

          {/* Pagination */}
          {pagination && (
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-bits-grey-600">
                Page {pagination.currentPage} of {pagination.totalPages} •{" "}
                {pagination.totalItems} items
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || projectsLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.totalPages || projectsLoading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
                <Select
                  value={String(limit)}
                  onValueChange={(v) => {
                    setLimit(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 30, 50].map((l) => (
                      <SelectItem key={l} value={String(l)}>
                        {l} / page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      );
    },
    [
      inputValue,
      handleSearchChange,
      projects,
      projectsLoading,
      projectsError,
      pagination,
      page,
      limit,
      navigate,
      viewMode,
      columns,
    ],
  );

  const TemplatesTab = () => {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [templateSearchQuery, setTemplateSearchQuery] = useState("");
    const [templateDifficultyFilter, setTemplateDifficultyFilter] = useState<
      string | undefined
    >(undefined);
    const [templateCategoryFilter, setTemplateCategoryFilter] = useState<
      string | undefined
    >(undefined);
    const [templateSubcategoryFilter, setTemplateSubcategoryFilter] = useState<
      string | undefined
    >(undefined);
    const [templateInputValue, setTemplateInputValue] = useState("");

    // Fetch categories and subcategories

    // Get unique categories and subcategories
    const uniqueCategories = useMemo(() => {
      // if (!categoriesData) return [];
      // const unique = Array.from(
      //   new Set(categoriesData.map((item) => item.category)),
      // );
      // return unique.filter(Boolean).sort();
      return [];
    }, []);

    const uniqueSubcategories = useMemo(() => {
      // if (!subcategoriesData) return [];
      // const unique = Array.from(
      //   new Set(subcategoriesData.map((item) => item.subcategory)),
      // );
      // return unique.filter(Boolean).sort();
      return [];
    }, []);

    // Debounce the template search input
    const debouncedTemplateSearch = useDebounce(templateInputValue, 1000);

    // Sync search query with debounced value
    useEffect(() => {
      setTemplateSearchQuery(debouncedTemplateSearch);
      setPage(1);
    }, [debouncedTemplateSearch]);

    // Sync input value when search query changes externally
    useEffect(() => {
      setTemplateInputValue(templateSearchQuery);
    }, [templateSearchQuery]);

    // Handle template search input changes
    const handleTemplateSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setTemplateInputValue(e.target.value);
      },
      [],
    );

    // Update params to use state values
    const params: GetTemplatesParams = useMemo(
      () => ({
        page,
        limit,
        ...(templateSearchQuery ? { search: templateSearchQuery } : {}),
        ...(templateDifficultyFilter
          ? { difficultyLevel: templateDifficultyFilter as any }
          : {}),
        ...(templateCategoryFilter ? { category: templateCategoryFilter } : {}),
        ...(templateSubcategoryFilter
          ? { subcategory: templateSubcategoryFilter }
          : {}),
      }),
      [
        page,
        limit,
        templateSearchQuery,
        templateDifficultyFilter,
        templateCategoryFilter,
        templateSubcategoryFilter,
      ],
    );

    const {
      data: templateData,
      isLoading,
      isError,
    } = useProjectTemplatesQuery(params);

    // Extract pagination data
    const pagination = templateData
      ? {
          page: (templateData as any)?.pagination?.page,
          pages: Math.ceil(
            ((templateData as any)?.pagination?.total || 0) /
              ((templateData as any)?.pagination?.limit || 10),
          ),
          total: (templateData as any)?.pagination?.total,
        }
      : null;
    // console.log(templateData);
    return (
      <div className="space-y-6">
        {/* Search and Filters */}
        <Card className="border">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4 justify-between">
              <h2 className="text-lg font-medium text-bits-grey">
                dataset Templates
              </h2>
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-40 w-80">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search Templates..."
                    className="pl-10"
                    value={templateInputValue}
                    onChange={handleTemplateSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
                <Select
                  value={templateCategoryFilter || "all-categories"}
                  onValueChange={(v) => {
                    setTemplateCategoryFilter(
                      v === "all-categories" ? undefined : v,
                    );
                    setTemplateSubcategoryFilter(undefined); // Reset subcategory when category changes
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-categories">
                      All Categories
                    </SelectItem>
                    {uniqueCategories.map((category) => (
                      <SelectItem
                        key={category as string}
                        value={category as string}
                      >
                        {category as string}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={templateSubcategoryFilter || "all-subcategories"}
                  onValueChange={(v) => {
                    setTemplateSubcategoryFilter(
                      v === "all-subcategories" ? undefined : v,
                    );
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="All Subcategories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-subcategories">
                      All Subcategories
                    </SelectItem>
                    {uniqueSubcategories.map((subcategory) => (
                      <SelectItem
                        key={String(subcategory)}
                        value={String(subcategory)}
                      >
                        {String(subcategory)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={templateDifficultyFilter || "all-difficulties"}
                  onValueChange={(v) => {
                    setTemplateDifficultyFilter(
                      v === "all-difficulties" ? undefined : v,
                    );
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full sm:w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-difficulties">
                      All Difficulties
                    </SelectItem>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Templates Grid */}
        <Card className="p-4">
          {isLoading ? (
            <div className="text-center py-12 text-bits-grey-600">
              Loading templates...
            </div>
          ) : isError ? (
            <div className="text-center py-12 text-bits-red text-sm">
              Failed to load templates
            </div>
          ) : !(templateData as any)?.templates ||
            (templateData as any).templates.length === 0 ? (
            <div className="text-center py-12 text-bits-grey-600 text-sm">
              No templates found
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
              {(templateData as any)?.templates?.map((template: any) => (
                <Card
                  key={template.id}
                  className="bg-white border border-gray-200"
                >
                  <CardContent className="p-6">
                    {/* Three Column Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                      {/* Column 1 - Title and Details (5 cols) */}
                      <div className="lg:col-span-5 space-y-3">
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-medium">
                            {template.template_name}
                          </h3>
                          {getStatusBadge("active")}
                        </div>

                        <p className="text-sm text-bits-grey-600">
                          {template.description}
                        </p>

                        <div className="flex flex-col gap-2 text-sm text-bits-grey-600">
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-gray-600 hover:text-gray-800" />
                            <span>Created by {template.createdBy.name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 text-gray-600 hover:text-gray-800" />
                            <span>
                              Last used:{" "}
                              {template?.last_used
                                ? new Date(
                                    template.last_used,
                                  ).toLocaleDateString()
                                : "-"}
                            </span>
                          </div>
                          {template.usage_count > 0 && (
                            <div className="text-body-sm font-medium text-bits-grey-600">
                              Used {template.usage_count} times
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Column 2 - Tags and Template Details (5 cols) */}
                      <div className="lg:col-span-5 space-y-4 border-l border-r px-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-semibold text-bits-grey-700 mb-1">
                              Category
                            </p>
                            <p className="text-sm text-bits-grey-600">
                              {template.category || "Not specified"}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-bits-grey-700 mb-1">
                              Subcategory
                            </p>
                            <p className="text-sm text-bits-grey-600">
                              {template.subcategory || "Not specified"}
                            </p>
                          </div>
                        </div>

                        {template.skills_covered &&
                          template.skills_covered.length > 0 && (
                            <div>
                              <p className="text-sm font-semibold text-bits-grey-700 mb-2">
                                Skills Covered
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {template.skills_covered.map(
                                  (skill: string, index: number) => (
                                    <span
                                      key={index}
                                      className="px-3 py-1 bg-blue-50 text-bits-blue-800 text-xs rounded-full border border-blue-200"
                                    >
                                      {skill}
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                        {template.technologies_used &&
                          template.technologies_used.length > 0 && (
                            <div>
                              <p className="text-sm font-semibold text-bits-grey-700 mb-2">
                                Technologies Used
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {template.technologies_used.map(
                                  (tech: string, index: number) => (
                                    <span
                                      key={index}
                                      className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full border border-indigo-200"
                                    >
                                      {tech}
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                      </div>

                      {/* Column 3 - Action Buttons (2 cols) */}
                      <div className="lg:col-span-2 flex flex-col justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            navigate(
                              `${paths.instructorProjectDetails.replace(
                                ":projectId",
                                template.id,
                              )}?mode=template`,
                            );
                          }}
                          className="text-bits-grey-700 w-full justify-start"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // console.log("Edit clicked");
                            // console.log(template);
                            navigate(
                              `${paths.editProject.replace(
                                ":projectId",
                                template.id,
                              )}?mode=template&action=edit`,
                            );
                          }}
                          className="text-bits-grey-700 w-full justify-start"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTemplate(template.id)}
                          disabled={isDeletingTemplate}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full justify-start"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          {isDeletingTemplate ? "Deleting..." : "Delete"}
                        </Button>
                        <Button
                          className="bg-bits-blue w-full"
                          size="sm"
                          onClick={() => handleUseTemplate(template.id)}
                        >
                          Use Template
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </Card>
        {/* Pagination */}
        {pagination && (
          <div className="flex items-center justify-between pt-2 mt-4 w-full">
            <div className="text-sm text-bits-grey-600">
              Page {pagination.page} of {pagination.pages} • {pagination.total}{" "}
              items
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pagination.pages || isLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
              <Select
                value={String(limit)}
                onValueChange={(v) => {
                  setLimit(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 50].map((l) => (
                    <SelectItem key={l} value={String(l)}>
                      {l} / page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>
    );
  };
  const AnalyticsTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="bg-white border border-gray-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center font-medium gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-bits-blue" />
            <span className="text-lg font-medium text-bits-grey">
              dataset Completion Trends
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center mb-6">
            <div className="text-3xl font-bold text-bits-green mb-2">85%</div>
            <div className="text-sm text-bits-grey-600">
              Average completion rate
            </div>
          </div>
          <div className="space-y-3">
            {[
              { label: "This Month", value: "92%" },
              { label: "Last Month", value: "88%" },
              { label: "3 Months Ago", value: "76%" },
            ].map((item) => (
              <div
                key={item.label}
                className="flex justify-between items-center"
              >
                <span className="text-sm font-medium">{item.label}</span>
                <span className="text-sm font-normal text-bits-grey-600">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-gray-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center font-medium gap-2 text-lg">
            <Layers className="h-5 w-5 text-bits-warning" />
            <span className="text-lg font-medium text-bits-grey">
              Popular Technologies
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { name: "Python", value: 95 },
              { name: "Scikit-learn", value: 85 },
              { name: "Pandas", value: 92 },
              { name: "TensorFlow", value: 68 },
            ].map((tech) => (
              <div
                key={tech.name}
                className="flex justify-between items-center"
              >
                <span className="text-md font-normal text-bits-grey-600">
                  {tech.name}
                </span>
                <div className="flex items-center gap-2 w-[40%]">
                  <Progress
                    value={tech.value}
                    className="h-2 text-xs font-normal text-bits-grey-600"
                  />
                  <span className="text-sm text-bits-grey-600">
                    {tech.value}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2 bg-white border border-gray-200">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center font-medium gap-2 text-lg">
            <Clock className="h-5 w-5 text-bits-blue" />
            <span className="text-lg font-medium text-bits-grey">
              Recent Activity
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              {
                icon: CheckCircle,
                iconColor: "text-bits-green",
                bgColor: "bg-bits-green-success-25",
                title: "dataset completed",
                description:
                  "Neural Networks Implementation - 20/20 students submitted",
                time: "2 hours ago",
              },
              {
                icon: AlertTriangle,
                iconColor: "text-bits-orange",
                bgColor: "bg-bits-warning-50",
                title: "Deadline approaching",
                description:
                  "Mall Customers Part 1 due in 5 days - 42/55 submitted",
                time: "3 hours ago",
              },
              {
                icon: Plus,
                iconColor: "text-bits-blue-500",
                bgColor: "bg-bits-blue-50",
                title: "New template created",
                description: 'Dr. A. Sharma created "Advanced NLP Pipeline"',
                time: "2 days ago",
              },
            ].map((activity, index) => (
              <div
                key={index}
                className={`flex items-start gap-3 p-3 rounded-lg ${activity.bgColor}`}
              >
                <activity.icon
                  className={`h-4 w-4 ${activity.iconColor} mt-1`}
                />
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-md font-medium">
                        {activity.title}
                      </div>
                      <div className="text-sm font-normal text-bits-grey-600 mt-1">
                        {activity.description}
                      </div>
                    </div>
                    <span className="text-xs text-bits-grey-600">
                      {activity.time}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  interface ShowSelectTemplateModalProps {
    isOpenSelectTemplate: boolean;
    setIsSelectTemplate: (open: boolean) => void;
  }

  const ShowSelectTemplateModal = ({
    isOpenSelectTemplate,
    setIsSelectTemplate,
  }: ShowSelectTemplateModalProps) => {
    const [selectedTemplate, setSelectedTemplate] = useState<number | null>(
      null,
    );
    const templates: TemplateData[] = [];

    return (
      <Dialog open={isOpenSelectTemplate} onOpenChange={setIsSelectTemplate}>
        <DialogContent className="sm:max-w-2xl bg-white border">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              Select a Template
            </DialogTitle>
            <DialogDescription>
              Create a dataset from scratch or get started faster with a
              template.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-600 hover:text-gray-800" />
              <Input placeholder="Search dataset template" className="pl-10" />
            </div>
            <Select defaultValue="new-first">
              <SelectTrigger className="w-40 flex-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new-first">Newest First</SelectItem>
                <SelectItem value="later">Later</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
            {templates?.map((i, index) => (
              <Card
                key={index}
                onClick={() => setSelectedTemplate(index)}
                className={`p-3 cursor-pointer transition border ${
                  selectedTemplate === index
                    ? "border-bits-blue"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-5">
                  <FileText className="h-5 w-5 text-bits-blue" />
                  <div className="flex-col">
                    <CardTitle className="font-medium text-base">
                      {i.name}
                    </CardTitle>
                    <p className="text-sm text-bits-grey-600">
                      Created at {i.createdAt}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </Card>

          <div className="flex gap-2">
            <Button
              onClick={() => setIsSelectTemplate(false)}
              className="flex-1"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={selectedTemplate === null}
              onClick={() => {
                setIsSelectTemplate(false);
              }}
              className="flex-1 bg-bits-blue hover:bg-bits-blue"
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  interface CreateNewProjectModalProps {
    isOpen: boolean;
    selectedOption: "newProject" | "useTemplate";
    onClose: () => void;
    onOptionChange: (option: "newProject" | "useTemplate") => void;
    onContinue: () => void;
  }

  const CreateNewProjectModal = memo(
    ({
      isOpen,
      selectedOption,
      onClose,
      onOptionChange,
      onContinue,
    }: CreateNewProjectModalProps) => {
      if (!isOpen) return null;

      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-gray-400/20" onClick={onClose} />
          <div className="relative z-10 w-full max-w-lg bg-white rounded-lg shadow-xl p-6">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-gray-900">
                  Add New Dataset
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Create a dataset from scratch or get started faster with a
                template.
              </p>
            </div>

            <div className="mb-6">
              <RadioGroup value={selectedOption} onValueChange={onOptionChange}>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="newProject" id="newProject" />
                    <Label
                      htmlFor="newProject"
                      className="text-base font-medium cursor-pointer"
                    >
                      Add New Dataset
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="useTemplate" id="useTemplate" />
                    <Label
                      htmlFor="useTemplate"
                      className="text-base font-medium cursor-pointer"
                    >
                      Use Template
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={onContinue}
                className="flex-1 bg-bits-blue hover:bg-bits-blue-700"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      );
    },
  );

  CreateNewProjectModal.displayName = "CreateNewProjectModal";

  interface LinkProjectModalProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    projectId: string;
    platformId?: string;
    createdBy?: string;
  }

  const LinkProjectModal = ({
    isOpen,
    setIsOpen,
    projectId,
    platformId,
    createdBy,
  }: LinkProjectModalProps) => {
    const { courseId, resourceLinkId, clearProjectLinkParams } =
      useProjectLink();
    const linkProjectMutation = useLinkProjectMutation();

    // State management
    const [selectedCourse, setSelectedCourse] = useState<string>("");
    const [selectedInstructorIds, setSelectedInstructorIds] = useState<
      string[]
    >([]);
    const [selectedTaIds, setSelectedTaIds] = useState<string[]>([]);
    const [selectedAssignment, setSelectedAssignment] = useState<string>("");

    // Auto-populate from URL parameters
    useEffect(() => {
      if (isOpen && courseId) {
        setSelectedCourse(courseId);
      }
    }, [isOpen, courseId]);

    useEffect(() => {
      if (isOpen && resourceLinkId && selectedCourse) {
        setSelectedAssignment(resourceLinkId);
      }
    }, [isOpen, resourceLinkId, selectedCourse]);

    // Fetch courses for linking
    const { data: contextsData, isLoading: contextsLoading } =
      useLTIContextsQuery({ enabled: isOpen, forLinking: true });
    const contexts = Array.isArray(contextsData) ? contextsData : [];

    // Get platform ID from selected course
    const selectedContext = contexts.find((ctx) => ctx.id === selectedCourse);
    const selectedPlatformId = selectedContext?.platformId || platformId;

    // Fetch instructors and TAs for selected course
    const { data: instructorsData = [], isLoading: instructorsLoading } =
      useLTIContextMembersQuery(
        {
          contextId: selectedCourse,
          platformId: selectedPlatformId,
          role: `${ROLES.INSTRUCTOR},${ROLES.TA}`,
        },
        isOpen && !!selectedCourse && !!selectedPlatformId,
      );
    const instructors = Array.isArray(instructorsData) ? instructorsData : [];
    useEffect(() => {
      // 1. Ensure instructorsData is ready and createdBy exists
      if (!createdBy || !Array.isArray(instructorsData)) {
        return;
      }

      // 2. Filter instructors (only those with roleName === "instructor")
      const instructorList = instructorsData.filter(
        (item) => item.roleName === "instructor",
      );

      // 3. Check if createdBy ID exists in the filtered list
      const isCreatedByValidInstructor = instructorList.some(
        (instructor) => instructor.id === createdBy,
      );

      // 4. If valid and not already selected, add it
      if (
        isCreatedByValidInstructor &&
        !selectedInstructorIds.includes(createdBy)
      ) {
        setSelectedInstructorIds((prev) => [...prev, createdBy]);
      }
    }, [instructorsData, createdBy]);

    // Fetch assignments for selected course
    const { data: assignmentsData = [], isLoading: assignmentsLoading } =
      useUnassignedResourceLinksQuery(
        {
          contextId: selectedCourse,
          platformId: selectedPlatformId,
        },
        isOpen && !!selectedCourse && !!selectedPlatformId,
      );
    const assignments = Array.isArray(assignmentsData) ? assignmentsData : [];

    // Separate instructors and TAs
    const instructorList = instructors.filter(
      (m) => m.roleName === "instructor",
    );
    const taList = instructors.filter((m) => m.roleName === "ta");

    // Event handlers
    const handleClose = () => {
      setIsOpen(false);
      setSelectedCourse("");
      setSelectedInstructorIds([]);
      setSelectedTaIds([]);
      setSelectedAssignment("");
      clearProjectLinkParams();
    };

    const handleCourseChange = (courseId: string) => {
      setSelectedCourse(courseId);
      setSelectedInstructorIds([]);
      setSelectedTaIds([]);
      setSelectedAssignment("");
    };

    const toggleInstructor = (instructorId: string) => {
      setSelectedInstructorIds((prev) =>
        prev.includes(instructorId)
          ? prev.filter((id) => id !== instructorId)
          : [...prev, instructorId],
      );
    };

    const toggleTA = (taId: string) => {
      setSelectedTaIds((prev) =>
        prev.includes(taId)
          ? prev.filter((id) => id !== taId)
          : [...prev, taId],
      );
    };

    const handleLinkProject = async () => {
      if (
        !selectedCourse ||
        !selectedAssignment ||
        (selectedInstructorIds.length === 0 && selectedTaIds.length === 0)
      ) {
        toast.error(
          "Please select a course, assignment, and at least one instructor or TA",
        );
        return;
      }

      try {
        await linkProjectMutation.mutateAsync({
          resourceLinkId: selectedAssignment,
          projectId,
          instructor_ids: selectedInstructorIds,
          teaching_ass_id: selectedTaIds,
        });
        clearProjectLinkParams();
        toast.success("dataset linked successfully!");
        handleClose();
      } catch (error) {
        toast.error("Failed to link dataset. Please try again.");
      }
    };

    const isLinkDisabled =
      !selectedCourse ||
      !selectedAssignment ||
      (selectedInstructorIds.length === 0 && selectedTaIds.length === 0) ||
      linkProjectMutation.isPending;

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="max-w-sm bg-white border transparent-overlay shadow-lg max-h-[95vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-lg font-semibold">
              Link dataset to LMS Assignment
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-2 overflow-x-visible">
            {/* Step 1: Course Selection */}
            <div>
              <Label htmlFor="course" className="text-sm font-medium">
                Course <span className="text-red-500">*</span>
              </Label>
              <Select
                value={selectedCourse}
                onValueChange={handleCourseChange}
                disabled={contextsLoading}
              >
                <SelectTrigger id="course" className="mt-2 w-full">
                  <SelectValue
                    placeholder={
                      contextsLoading ? "Loading courses..." : "Select Course"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="z-[150]">
                  {contextsLoading ? (
                    <SelectItem value="loading" disabled>
                      Loading...
                    </SelectItem>
                  ) : contexts.length === 0 ? (
                    <SelectItem value="no-courses" disabled>
                      No courses available
                    </SelectItem>
                  ) : (
                    contexts.map((context) => (
                      <SelectItem key={context.id} value={context.id}>
                        {context.contextTitle} ({context.contextLabel})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Instructors Selection */}
            {selectedCourse && (
              <div>
                <Label className="text-sm font-medium">
                  Instructors <span className="text-red-500">*</span>
                </Label>
                <Card className="mt-2">
                  <CardContent className="p-3">
                    {instructorsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                        <span className="ml-2 text-sm text-muted-foreground">
                          Loading...
                        </span>
                      </div>
                    ) : instructorList.length === 0 ? (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        No instructors found
                      </div>
                    ) : (
                      <>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {instructorList.map((instructor) => (
                            <div
                              key={instructor.id}
                              className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                              onClick={() => toggleInstructor(instructor.id)}
                            >
                              <input
                                type="checkbox"
                                checked={selectedInstructorIds.includes(
                                  instructor.id,
                                )}
                                onChange={() => {}}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {instructor.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {instructor.email}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {selectedInstructorIds.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs text-blue-600 font-medium">
                              {selectedInstructorIds.length} instructor
                              {selectedInstructorIds.length > 1 ? "s" : ""}{" "}
                              selected
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Teaching Assistants Selection */}
            {selectedCourse && (
              <div>
                <Label className="text-sm font-medium">
                  Teaching Assistants
                </Label>
                <Card className="mt-2">
                  <CardContent className="p-3">
                    {instructorsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                        <span className="ml-2 text-sm text-muted-foreground">
                          Loading...
                        </span>
                      </div>
                    ) : taList.length === 0 ? (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        No teaching assistants found
                      </div>
                    ) : (
                      <>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {taList.map((ta) => (
                            <div
                              key={ta.id}
                              className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                              onClick={() => toggleTA(ta.id)}
                            >
                              <input
                                type="checkbox"
                                checked={selectedTaIds.includes(ta.id)}
                                onChange={() => {}}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {ta.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {ta.email}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {selectedTaIds.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs text-blue-600 font-medium">
                              {selectedTaIds.length} TA
                              {selectedTaIds.length > 1 ? "s" : ""} selected
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 4: Assignment Selection */}
            {selectedCourse && (
              <div className="relative z-10">
                <Label htmlFor="assignment" className="text-sm font-medium">
                  Assignment <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={selectedAssignment}
                  onValueChange={setSelectedAssignment}
                  disabled={assignmentsLoading}
                >
                  <SelectTrigger
                    id="assignment"
                    className="mt-2 w-full bg-white"
                  >
                    <SelectValue
                      placeholder={
                        assignmentsLoading
                          ? "Loading assignments..."
                          : "Select Assignment"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[9999] bg-white">
                    {assignmentsLoading ? (
                      <SelectItem value="loading" disabled>
                        Loading...
                      </SelectItem>
                    ) : assignments.length === 0 ? (
                      <SelectItem value="no-assignments" disabled>
                        No assignments available
                      </SelectItem>
                    ) : (
                      assignments.map((assignment) => (
                        <SelectItem key={assignment.id} value={assignment.id}>
                          {assignment.resourceLinkTitle}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex gap-2 mt-6 flex-shrink-0">
            <Button
              onClick={handleClose}
              variant="outline"
              className="flex-1"
              disabled={linkProjectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLinkProject}
              className="flex-1 bg-bits-blue hover:bg-bits-blue-700"
              disabled={isLinkDisabled}
            >
              {linkProjectMutation.isPending ? "Linking..." : "Link dataset"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  const DeleteProjectModal = ({
    isOpen,
    onClose,
    projectName = "Untitled dataset",
    projectId,
  }: {
    isOpen: boolean;
    onClose: () => void;
    projectName?: string;
    projectId: string;
  }) => {
    const { mutate: deleteProjectMutation, isPending } = useDeleteProject();
    const handleDelete = () => {
      deleteProjectMutation(projectId, {
        onSuccess: () => {
          toast.success("dataset deleted successfully");
          onClose();
        },
        onError: () => {
          onClose();
          toast.error("Failed to delete dataset");
        },
      });
    };

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md bg-white border transparent-overlay">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-black text-[18px]">
              Delete dataset?
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <p className="text-gray-700 mb-4 text-[14px]">
              Are you sure you want to delete{" "}
              <span className="font-semibold">{projectName}</span>?
            </p>
          </div>

          <div className="flex flex-row-reverse gap-4 mt-6">
            <button
              onClick={handleDelete}
              disabled={isPending}
              className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors font-medium"
            >
              {isPending ? "Deleting..." : "Delete"}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };
  const CloseProjectModal = ({
    isOpen,
    onClose,
    projectName = "Untitled dataset",
    projectId,
  }: {
    isOpen: boolean;
    onClose: () => void;
    projectName?: string;
    projectId: string;
  }) => {
    const { mutate: closeProjectMutation, isPending: isClosePending } =
      useCloseProject();

    const handleClose = () => {
      closeProjectMutation(projectId, {
        onSuccess: (response) => {
          const message = response?.data?.data?.error?.message;
          if (message) {
            toast.info(message);
          } else {
            toast.success("dataset closed successfully");
          }
          onClose();
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.data?.message ||
            error?.response?.data?.message ||
            "Failed to close dataset";
          toast.error(message);
          onClose();
        },
      });
    };

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-md bg-white border transparent-overlay">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-black text-[18px]">
              Close dataset?
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <p className="text-gray-700 mb-4 text-[14px]">
              Are you sure you want to close{" "}
              <span className="font-semibold">{projectName}</span>?
            </p>
          </div>

          <div className="flex flex-row-reverse gap-4 mt-6">
            <button
              onClick={handleClose}
              disabled={isClosePending}
              className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors font-medium"
            >
              {isClosePending ? "Closing..." : "Close"}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  };
  const { mutateAsync: duplicateTemplate } = useDuplicateProjectTemplate();

  const handleUseTemplate = async (templateId: string) => {
    try {
      toast.info("Duplicating template...");
      const response = await duplicateTemplate(templateId);
      if (response?.isSuccess) {
        toast.success(response.message || "Template duplicated successfully!");
        refetchProjects();
        setActiveTab("all-projects");
      } else {
        toast.error(
          response?.data?.data?.message || "Failed to duplicate template",
        );
      }
    } catch (error) {
      toast.error("Failed to duplicate template");
    }
  };

  return (
    <div className="p-6 bg-white min-h-screen">
      <PageHeader title="Manage Datasets" subtitle="">
        <Button
          className="bg-bits-blue hover:bg-bits-blue-700"
          onClick={() => setIsModalOpen2(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          <span className="text-sm font-semibold">Add New Dataset</span>
        </Button>
      </PageHeader>

      <AddNewDatasetPage
        isOpen={isModalOpen2}
        selectedOption={creationType}
        onClose={handleModalClose}
        onOptionChange={(option) =>
          setCreationType(option as "newProject" | "useTemplate")
        }
        onContinue={handleContinue}
        onSuccess={() => {
          refetchProjects();
          toast.success("Dataset created successfully!");
        }}
      />

      <SelectProjectTemplate
        isOpen={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
      />

      <LinkProjectModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        projectId={selectedProjectIdToLink}
        createdBy={selectedCreatedBy}
      />

      {(activeTab === "all-projects" || activeTab === "datasets") &&
        renderAllProjectsTab({
          projects: enhancedDatasetsData?.datasets,
          pagination: enhancedDatasetsData?.pagination,
          projectsLoading,
          projectsError,
          page,
          setPage,
          limit,
          setLimit,
          inputValue,
          handleSearchChange,
          navigate,
        })}
      {activeTab === "templates" && <TemplatesTab />}
      {activeTab === "analytics" && <AnalyticsTab />}

      {isOpenSelectTemplate && (
        <ShowSelectTemplateModal
          isOpenSelectTemplate={isOpenSelectTemplate}
          setIsSelectTemplate={setIsSelectTemplate}
        />
      )}

      {templateDialogProjectId && (
        <ConvertToTemplateDialog
          isOpen={true}
          onOpenChange={(open) => {
            if (!open) setTemplateDialogProjectId(null);
          }}
          projectId={templateDialogProjectId}
        />
      )}

      {/* Dataset Edit Modal (matching ViewDatasetPage) */}
      {isEditModalOpen && (
        <AddNewDatasetPage
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={() => {
            refetchProjects();
            setIsEditModalOpen(false);
          }}
          dataset={selectedDatasetForEdit}
        />
      )}

      {/* Dataset Projects Warning Dialog */}
      <WarningDialog
        open={showProjectsWarning}
        title="Dataset is linked to projects"
        message={
          <div>
            <p>This dataset is used by the following project(s):</p>
            <ul className="mt-2 list-disc pl-5">
              {linkedProjects.map((proj, idx) => (
                <li key={idx} className="text-sm">
                  {proj}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-sm text-gray-600">
              Deleting this dataset may affect these projects. Unlink them then
              proceed with deletion.
            </p>
          </div>
        }
        onClose={() => {
          setShowProjectsWarning(false);
          setConfirmOpen(false);
        }}
      />

      {/* Dataset Delete Confirmation */}
      <ConfirmDialog
        open={confirmOpen}
        title={`Delete "${datasetToDelete?.title || "dataset"}"?`}
        description={
          <span>
            Are you sure you want to delete{" "}
            <strong>{datasetToDelete?.title}</strong>?{" "}
            {/* {linkedProjects.length > 0 && (
              <span className="text-orange-600">
                (This will also unlink it from {linkedProjects.length}{" "}
                associated project{linkedProjects.length !== 1 ? "s" : ""}.)
              </span>
            )} */}
          </span>
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        onConfirm={onConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {/* View Details Dialog */}
      <Dialog
        open={isViewDetailsDialogOpen}
        onOpenChange={setIsViewDetailsDialogOpen}
      >
        <DialogContent className="max-w-2xl bg-white border">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              Dataset Details
            </DialogTitle>
          </DialogHeader>
          {selectedDataset && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-medium">{selectedDataset.title}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Dataset ID: {selectedDataset.id}
                </p>
              </div>

              {selectedDataset.description && (
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <p className="text-sm text-gray-700 mt-1">
                    {selectedDataset.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <div className="mt-1">
                    {getStatusBadge(selectedDataset.status)}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Difficulty</Label>
                  <div className="mt-1">
                    {getDifficultyBadge(selectedDataset.difficultyLevel)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Created By</Label>
                  <p className="text-sm text-gray-700 mt-1">
                    {selectedDataset.createdBy?.name || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Course</Label>
                  <p className="text-sm text-gray-700 mt-1">
                    {selectedDataset.course?.name || "N/A"}
                  </p>
                </div>
              </div>

              {selectedDataset.tags && selectedDataset.tags.length > 0 && (
                <div>
                  <Label className="text-sm font-medium">Tags</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedDataset.tags.map((tag: string) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Files Count</Label>
                  <p className="text-sm text-gray-700 mt-1">
                    {selectedDataset.filesCount || 0}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Comments Count</Label>
                  <p className="text-sm text-gray-700 mt-1">
                    {selectedDataset.commentsCount || 0}
                  </p>
                </div>
              </div>

              {selectedDataset.dueDate && (
                <div>
                  <Label className="text-sm font-medium">Due Date</Label>
                  <p className="text-sm text-gray-700 mt-1">
                    {selectedDataset.dueDate}
                  </p>
                </div>
              )}

              {selectedDataset.coverPhotoUrl && (
                <div>
                  <Label className="text-sm font-medium">Cover Photo</Label>
                  <div className="mt-1">
                    <img
                      src={selectedDataset.coverPhotoUrl}
                      alt={selectedDataset.title}
                      className="w-32 h-32 object-cover rounded"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
